"use client";

import React, { useMemo, useReducer, useCallback, useContext, useState } from 'react';
import styles from './_hcp-gc-calculator.module.scss';
import {
  GC_CALC_STEP,
  GC_CALC_POPULATION,
  GC_CALC_STATUS,
  resolveActiveSteps,
  computeTotalHce,
} from '@/data/gc-calculator-data';
import Badge from '@/components/badge';

import { LayoutContext } from '@/components/layout-context-container/';
import Button from '@/components/button';
import CustomImage from '@/components/image';

// Components
import GcStepper from './components/gc-stepper';
import GcStepPill from './components/gc-step-pill';
import GcStepAnimatedCard from './components/gc-step-animated-card';

// Steps
import GcStepPatientProfile from './steps/gc-step-patient-profile';
import GcStepStatusInfo from './steps/gc-step-status-info';
import GcStepPatientExpectations from './steps/gc-step-patient-expectations';
import GcStepGcTherapyAdjustments from './steps/gc-step-gc-therapy-adjustments';
import GcStepStartingLabs from './steps/gc-step-starting-labs';
import GcStepMostRecentLabs from './steps/gc-step-most-recent-labs';
import GcStepAssessTreatmentProgress from './steps/gc-step-assess-treatment-progress';
import GcStepClinicalOptions from './steps/gc-step-clinical-options';
import GcStepSummary from './steps/gc-step-summary';
import PageAnchor from '@/components/page-anchor';

/**
 * @description HCP Dosing — GC OPTIMIZE™ Calculator
 * Inline multi-step wizard that guides HCPs through glucocorticoid dose
 * reductions when initiating or managing CRENESSITY in adult and pediatric
 * patients with classic CAH.
 *
 * Architecture:   .github/skills/gc-calculator/references/dev-plan.md
 * Data / steps:   src/data/gc-calculator-data.js
 * Clinical spec:  .github/skills/gc-calculator/SKILL.md
 */

// ---------------------------------------------------------------------------
// Step component map — keyed by GC_CALC_STEP constant
// ---------------------------------------------------------------------------
const STEP_COMPONENTS = {
  [GC_CALC_STEP.PATIENT_PROFILE]:           GcStepPatientProfile,
  [GC_CALC_STEP.STATUS_INFO]:               GcStepStatusInfo,
  [GC_CALC_STEP.PATIENT_EXPECTATIONS]:      GcStepPatientExpectations,
  [GC_CALC_STEP.GC_THERAPY_ADJUSTMENTS]:    GcStepGcTherapyAdjustments,
  [GC_CALC_STEP.STARTING_LABS]:             GcStepStartingLabs,
  [GC_CALC_STEP.MOST_RECENT_LABS]:          GcStepMostRecentLabs,
  [GC_CALC_STEP.ASSESS_TREATMENT_PROGRESS]: GcStepAssessTreatmentProgress,
  [GC_CALC_STEP.CLINICAL_OPTIONS]:          GcStepClinicalOptions,
  [GC_CALC_STEP.SUMMARY]:                   GcStepSummary,
};

// ---------------------------------------------------------------------------
// Wizard reducer
// ---------------------------------------------------------------------------
const INITIAL_STATE = {
  hasStarted: false,          // false = show intro screen; true = show wizard
  currentStepId: GC_CALC_STEP.PATIENT_PROFILE,
  expandedStepId: null,       // which step card is open (null = none expanded)
  population: null,   // GC_CALC_POPULATION.ADULT | PEDIATRIC — set after Step 1
  status: null,       // GC_CALC_STATUS.STARTING | CONTINUING  — set after Step 1
  formValues: {},     // keyed by field name, populated by each step
  hceTotal: 0,        // computed by computeTotalHce() in GcStepGcTherapyAdjustments
  calcId: null,       // unique session ID — generated on START_WIZARD
  reviewReturnStepId: null, // set when a transient step is shown during a review
};

function gcCalcReducer(state, action) {
  switch (action.type) {
    case 'START_WIZARD':
      // Auto-open the first step when entering the wizard
      return {
        ...state,
        hasStarted: true,
        expandedStepId: GC_CALC_STEP.PATIENT_PROFILE,
        calcId: crypto.randomUUID(),
      };
    case 'SET_STEP':
      // Transient step navigation — advance position only, no auto-open
      return { ...state, currentStepId: action.stepId };
    case 'OPEN_STEP':
      // Open a specific step card (review or start) without advancing position
      return { ...state, expandedStepId: action.stepId };
    case 'ADVANCE_STEP':
      // Saving the current step — advance position; expandedStepId controls which
      // card opens next (null = all collapsed, used for Summary and transient steps)
      return { ...state, currentStepId: action.nextStepId, expandedStepId: action.expandedStepId !== undefined ? action.expandedStepId : null };
    case 'SET_FIELD':
      return { ...state, formValues: { ...state.formValues, [action.field]: action.value } };
    case 'SET_POPULATION_STATUS':
      return { ...state, population: action.population, status: action.status };
    case 'SET_HCE_TOTAL':
      return { ...state, hceTotal: action.hceTotal };
    // Saves Step 1 fields atomically. If population or status changed from the
    // previously saved value, all downstream formValues and hceTotal are cleared
    // to prevent data inconsistency across steps that depend on this branching.
    case 'SAVE_PATIENT_PROFILE': {
      const valuesChanged =
        action.population !== state.population ||
        action.status     !== state.status;
      // Only treat as a re-route if the profile was previously saved
      // (population was already set). First-time save is not a re-route.
      const isReroute = state.population !== null && valuesChanged;
      const step1Values = {
        age:            action.age,
        status:         action.status,
        treatmentGoals: action.treatmentGoals,
      };
      if (isReroute) {
        // Reset position back to Patient Profile so all forward steps are
        // no longer considered "completed" — goNext (with forceReadvance)
        // will then advance to the correct next step in the new path.
        return {
          ...state,
          population:    action.population,
          status:        action.status,
          hceTotal:      0,
          currentStepId: GC_CALC_STEP.PATIENT_PROFILE,
          expandedStepId: GC_CALC_STEP.PATIENT_PROFILE,
          formValues:    step1Values,
        };
      }
      return {
        ...state,
        population: action.population,
        status:     action.status,
        hceTotal:   valuesChanged ? 0 : state.hceTotal,
        formValues: valuesChanged
          ? step1Values
          : { ...state.formValues, ...step1Values },
      };
    }
    // Saves Starting Labs atomically. If a4Assessment changed, clears all
    // downstream step data (GC therapy, assess progress, clinical options).
    case 'SAVE_STARTING_LABS': {
      const a4Changed = action.a4Assessment !== state.formValues.a4Assessment;
      const labsValues = {
        ...state.formValues,
        a4Assessment: action.a4Assessment,
      };
      if (a4Changed) {
        // Preserve only fields from steps before Starting Labs
        const { age, status, treatmentGoals, visitDate, labDrawDate, labDrawTime, labDrawRelative, clinicalAssessmentDate, expectedStartDate } = state.formValues;
        return {
          ...state,
          hceTotal: 0,
          formValues: {
            age, status, treatmentGoals, visitDate, labDrawDate, labDrawTime, labDrawRelative, clinicalAssessmentDate, expectedStartDate,
            a4Assessment: action.a4Assessment,
          },
        };
      }
      return { ...state, formValues: labsValues };
    }
    // Saves Most Recent Labs atomically. If a4Assessment changed, clears all
    // downstream step data (GC therapy, assess progress, clinical options).
    case 'SAVE_MOST_RECENT_LABS': {
      const a4Changed = action.a4Assessment !== state.formValues.a4Assessment;
      const labsValues = {
        ...state.formValues,
        a4Assessment: action.a4Assessment,
      };
      if (a4Changed) {
        // Preserve only fields from steps before Most Recent Labs
        const { age, status, treatmentGoals, visitDate, labDrawDate, labDrawTime, labDrawRelative } = state.formValues;
        return {
          ...state,
          hceTotal: 0,
          formValues: {
            age, status, treatmentGoals, visitDate, labDrawDate, labDrawTime, labDrawRelative,
            a4Assessment: action.a4Assessment,
          },
        };
      }
      return { ...state, formValues: labsValues };
    }
    // Enters a transient step (e.g. STATUS_INFO) during review mode.
    // Sets currentStepId to the transient step so isCurrentTransient triggers
    // the existing solo-card rendering, while preserving the original step in
    // reviewReturnStepId so it can be restored when the transient step saves.
    case 'ENTER_REVIEW_TRANSIENT':
      return {
        ...state,
        currentStepId:      action.stepId,
        expandedStepId:     null,
        reviewReturnStepId: action.returnStepId,
      };
    // Completes a review-mode transient step — restores the original currentStepId
    // and auto-opens that step's card so the user lands back where they left off.
    case 'END_REVIEW_TRANSIENT': {
      const returnId = state.reviewReturnStepId;
      return {
        ...state,
        currentStepId:      returnId,
        expandedStepId:     returnId !== GC_CALC_STEP.SUMMARY ? returnId : null,
        reviewReturnStepId: null,
      };
    }
    case 'RESET':
      return INITIAL_STATE;
    default:
      return state;
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
function HcpDosingGcCalculator({ ref, className }) {
  const { setAutoScrollActive } = useContext(LayoutContext);
  const [state, dispatch] = useReducer(gcCalcReducer, INITIAL_STATE);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isEmailSending, setIsEmailSending] = useState(false);

  const sectionClass = useMemo(() => {
    let cls = styles.gc_calculator;
    if (className) cls += ` ${className}`;
    return cls;
  }, [className]);

  // Build the ordered list of active steps based on current wizard state
  const activeSteps = useMemo(() => resolveActiveSteps(state), [state]);

  // Index of the current step within the active list
  const currentIndex = useMemo(
    () => activeSteps.findIndex((s) => s.id === state.currentStepId),
    [activeSteps, state.currentStepId]
  );

  const goToStep = useCallback((stepId) => {
    if (isAnimating) return;
    setIsAnimating(true);
    dispatch({ type: 'OPEN_STEP', stepId });
  }, [isAnimating]);

  const goBack = useCallback(() => {
    const idx = activeSteps.findIndex((s) => s.id === state.expandedStepId);
    const prev = idx > 0 ? activeSteps[idx - 1] : null;
    if (prev) dispatch({ type: 'OPEN_STEP', stepId: prev.id });
  }, [activeSteps, state.expandedStepId]);

  const getCalcScrollTarget = useCallback(() => {
    const sectionEl = document.querySelector('[data-gc-calculator-root]');
    if (sectionEl) return sectionEl;

    const isTabletSmall = window.matchMedia('(max-width: 767px)').matches;
    return isTabletSmall
      ? document.querySelector('[data-gc-calc-col]')
      : document.getElementById('gc-calculator');
  }, []);

  // Scroll to the top of the calculator section to reorient the user after a
  // step collapses (content removed from DOM shifts the apparent scroll position).
  const scrollToCalcTop = useCallback(() => {
    const getTargetScrollY = () => {
      const isTabletSmall = window.matchMedia('(max-width: 767px)').matches;
      const el = getCalcScrollTarget();
      if (!el) return null;
      const topOffset = isTabletSmall ? 24 : 32;
      return el.getBoundingClientRect().top + window.scrollY - topOffset;
    };

    setTimeout(() => {
      // First pass keeps current behavior responsive to user interaction.
      const top = getTargetScrollY();
      if (top === null) return;
      setAutoScrollActive(true);
      window.scrollTo({ top, behavior: 'smooth' });
    }, 333);

    setTimeout(() => {
      // Second pass corrects drift after collapse animation completes,
      // but only fires if the layout has shifted significantly (>10px)
      // to avoid a jarring re-scroll when position is already correct.
      const top = getTargetScrollY();
      if (top === null || Math.abs(window.scrollY - top) <= 10) return;
      setAutoScrollActive(true);
      window.scrollTo({ top, behavior: 'smooth' });
    }, 950);
  }, [getCalcScrollTarget, setAutoScrollActive]);

  // stateOverride lets steps pass values they just dispatched (e.g. population/status)
  // so the path resolves correctly before the reducer re-renders.
  const goNext = useCallback((stateOverride = {}) => {
    const effectiveState = { ...state, ...stateOverride };
    const steps = resolveActiveSteps(effectiveState);
    const isCurrentTransient = activeSteps[currentIndex]?.transient === true;
    const expandedId = isCurrentTransient ? state.currentStepId : state.expandedStepId;
    const idx = steps.findIndex((s) => s.id === expandedId);
    const next = steps[idx + 1];

    // Returns the step id to auto-open after advancing, or null when the next
    // step should not open automatically:
    //   • Summary renders inline (not a card) — always show all collapsed
    //   • Transient steps render solo — pill list not visible
    const getAutoOpenId = (step) =>
      step && !step.transient && step.id !== GC_CALC_STEP.SUMMARY ? step.id : null;

    if (isCurrentTransient) {
      if (state.reviewReturnStepId) {
        // Review-mode transient step (e.g. STATUS_INFO) completing — restore the
        // original currentStepId and auto-open that card. Pill list remounts fresh
        // so no animation lock is needed.
        dispatch({ type: 'END_REVIEW_TRANSIENT' });
      } else if (next) {
        // First-time transient step: advance past it and auto-open the next card.
        // Pill list remounts fresh — no animation machine fires, setIsAnimating
        // is intentionally not called here.
        dispatch({ type: 'ADVANCE_STEP', nextStepId: next.id, expandedStepId: getAutoOpenId(next) });
      }
    } else if (expandedId === state.currentStepId) {
      // Saving the next-available step: advance and auto-open the next card.
      if (next) {
        dispatch({ type: 'ADVANCE_STEP', nextStepId: next.id, expandedStepId: getAutoOpenId(next) });
        // If next step is transient, the pill list unmounts (animated cards
        // destroyed) so onAnimationComplete will never fire — unlock immediately.
        if (!next.transient) setIsAnimating(true);
      } else {
        dispatch({ type: 'OPEN_STEP', stepId: null }); // last step — just collapse
        setIsAnimating(true);
      }
    } else if (stateOverride.forceReadvance && next) {
      // Re-saving a routing-critical step (e.g. population/status change) —
      // reset position to the corrected path and auto-open the next card.
      dispatch({ type: 'ADVANCE_STEP', nextStepId: next.id, expandedStepId: getAutoOpenId(next) });
      if (!next.transient) setIsAnimating(true);
    } else {
      // Re-saving a reviewed completed step.
      if (next?.transient) {
        // Next step is transient (e.g. STATUS_INFO on adult-starting path) —
        // set currentStepId to it so the existing isCurrentTransient path renders
        // it solo. reviewReturnStepId stores the original step to restore after.
        dispatch({ type: 'ENTER_REVIEW_TRANSIENT', stepId: next.id, returnStepId: state.currentStepId });
        // No isAnimating lock — transient view unmounts the pill list
      } else {
        // Collapse back to the condensed pill list.
        // Next steps are never reset — they stay in their current completed state.
        dispatch({ type: 'OPEN_STEP', stepId: null });
        setIsAnimating(true);
      }
    }

    // Scroll back to the calculator top so the collapsed pill list is in view
    scrollToCalcTop();
  }, [state, activeSteps, currentIndex, scrollToCalcTop]);

  const handleStart = useCallback(() => {
    dispatch({ type: 'START_WIZARD' });
  }, []);

  const handleReset = useCallback(() => {
    dispatch({ type: 'RESET' });
    setTimeout(() => {
      const el = getCalcScrollTarget();
      if (!el) return;
      const isTabletSmall = window.matchMedia('(max-width: 767px)').matches;
      const top = el.getBoundingClientRect().top + window.scrollY - (isTabletSmall ? 24 : 32);
      setAutoScrollActive(true);
      window.scrollTo({ top, behavior: 'smooth' });
    }, 333);
  }, [getCalcScrollTarget, setAutoScrollActive]);

  const ActiveStep = STEP_COMPONENTS[state.currentStepId];

  // Transient steps (e.g. Status Info) render solo — no pill list
  const currentStep = activeSteps[currentIndex];
  const isTransientStep = currentStep?.transient === true;

  return (
    <>
    <PageAnchor anchorId="gc-optimize" offsetY={-32} />
    <PageAnchor anchorId="gc-dosing-calculators" offsetY={-32} />
    <PageAnchor anchorId="gc-dosing-calculator" offsetY={-32} />
    <section
      ref={ref}
      className={sectionClass}
      data-gc-calculator-root
      data-gtm-event-category="gc-calculator"
    >
      <div className="container container--px-12">
        {/* Single persistent layout — image column never unmounts, preventing reload */}
        <div className={styles.calculator_layout}>

          {/* ── Left column — intro content OR active step ── */}
          <div className={styles.calc_left} data-gc-calc-col>
            <Badge ariaLabel="New">NEW</Badge>

            {!state.hasStarted ? (
              // ── Intro / default state ──────────────────────────────────
              <>
                <div className={styles.intro_content}>
                  <h2 className={styles.intro_heading}>
                    GC OPTIMIZE™ calculator
                  </h2>

                  <p className={styles.intro_description}>
                    Powered by expert recommendations, GC OPTIMIZE™ can help 
                    guide dose reductions when managing patients with classic CAH 
                    who are taking CRENESSITY for androgen control.
                  </p>

                  <p className={styles.intro_description}>
                    Develop a personalized GC dose reduction strategy at each 
                    visit using current GC doses, lab results, and your patient’s 
                    treatment goals.
                  </p>

                  <div className={styles.entry_card}>
                    <div className={styles.entry_card_text}>
                      {/* <span className={styles.entry_card_label}>Start here</span> */}
                      <span className={styles.entry_card_title}>Create a GC Down-Titration Plan</span>
                    </div>
                    <Button
                      type="button"
                      variant="solid"
                      customClass={styles.entry_card_btn}
                      onClick={handleStart}
                      data-gtm-event-category="gc-calculator"
                      data-gtm-event-action="click"
                      data-gtm-event-label="GC OPTIMIZE Calculator - Get Started"
                    >
                      Get Started
                    </Button>
                  </div>

                  <p className={styles.intro_disclaimer}>
                    As patients vary in clinical presentation, this calculator may
                    not be appropriate for every person with classic CAH. Adjustments to GC
                    doses should be individualized based on each patient&#39;s
                    clinical needs and the clinician&#39;s experience.
                  </p>

                </div>
              </>
            ) : (
              // ── Wizard ────────────────────────────────────────────────
              <>
                {isTransientStep ? (
                  // Transient step (e.g. Status Info) — solo card, no pills
                  ActiveStep && (
                    <ActiveStep
                      state={state}
                      dispatch={dispatch}
                      onBack={goBack}
                      onNext={goNext}
                      stepNumber={activeSteps.slice(0, currentIndex).filter(s => !s.transient).length}
                      innerStep={1}
                    />
                  )
                ) : (
                  // Normal step — accordion pill list
                  <div className={styles.step_list}>
                    {(() => {
                      // Exclude Summary from pill list — it renders inline below
                      const nonTransientSteps = activeSteps.filter((s) => !s.transient && s.id !== GC_CALC_STEP.SUMMARY);
                      return nonTransientSteps
                        .map((step, pillIdx) => {
                        const stepIdx = activeSteps.findIndex((s) => s.id === step.id);
                        const isCompleted = stepIdx >= 0 && stepIdx < currentIndex;
                        const isNextAvailable = step.id === state.currentStepId && !isCompleted;
                        const isExpanded = step.id === state.expandedStepId;

                        const isDisabled = !isCompleted && !isNextAvailable;
                        const StepComp = STEP_COMPONENTS[step.id];
                        return (
                          <GcStepAnimatedCard
                            key={step.id}
                            isExpanded={isExpanded}
                            variant={isDisabled ? 'disabled' : 'default'}
                            onAnimationComplete={() => setIsAnimating(false)}
                            pill={
                              <GcStepPill
                                step={step}
                                isCompleted={isCompleted}
                                isEnabled={isNextAvailable}
                                actionsDisabled={isEmailSending}
                                /* sublabel={pillIdx === 0 ? 'Start here' : undefined} */
                                onReview={isCompleted ? () => goToStep(step.id) : undefined}
                                onStart={isNextAvailable ? () => goToStep(step.id) : undefined}
                              />
                            }
                          >
                            {StepComp && (
                              <StepComp
                                state={state}
                                dispatch={dispatch}
                                onBack={goBack}
                                onNext={goNext}
                                // isReview={isCompleted && isExpanded} // Review mode only when clicking to expand a completed step — not on initial open of the next step
                                isReview={false}
                                stepNumber={pillIdx + 1}
                              />
                            )}
                          </GcStepAnimatedCard>
                        );
                      });
                    })()}

                    {state.currentStepId === GC_CALC_STEP.SUMMARY && (
                      <GcStepSummary
                        state={state}
                        dispatch={dispatch}
                        onBack={goBack}
                        onNext={goNext}
                        onSendingChange={setIsEmailSending}
                      />
                    )}

                    {currentIndex > 0 && (
                    <button
                      type="button"
                      className={styles.reset_btn}
                      onClick={handleReset}
                      disabled={isEmailSending}
                      data-gtm-event-category="gc-calculator"
                      data-gtm-event-action="Click"
                      data-gtm-event-label="Reset to start over"
                    >
                      Reset to start over
                      <svg width="15" height="14" viewBox="0 0 15 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
                        <path d="M12.04 7.034C12.53 6.6 13.286 6.152 14.266 5.69V6.502C13.118 7.482 12.278 8.518 11.746 9.582H11.34C10.808 8.518 9.954 7.482 8.82 6.502V5.69C9.786 6.152 10.542 6.6 11.046 7.034V7.02C11.046 4.234 8.792 1.994 6.02 1.994C3.234 1.994 0.994 4.234 0.994 7.02C0.994 9.806 3.234 12.046 6.02 12.046C7.938 12.046 9.422 10.954 10.066 10.002L10.808 10.66C9.926 11.92 8.148 13.04 6.02 13.04C2.688 13.04 0 10.352 0 7.02C0 3.688 2.688 1 6.02 1C9.352 1 12.04 3.688 12.04 7.02V7.034Z" fill="currentColor"/>
                      </svg>
                    </button>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          {/* ── Right column — always mounted so image never reloads ── */}
          <div className={`${styles.intro_image_col}${!state.hasStarted ? ` ${styles.intro_image_col_init}` : ''}`.trim()}>
            <CustomImage
              src="/images/components/hcp/gc-calc/Dosing_GC-Optimize-Calculator.png"
              alt=""
              width={382}
              height={366}
              className={styles.intro_image}
            />
          </div>
        </div>
      </div>
    </section>
    </>
  );
}

export default HcpDosingGcCalculator;
